"use client";

import { useContext, useState } from "react";
import { FullReport } from "@/components/internship-map/full-report";
import { AnonComments } from "@/components/common/anon-comments/anon-comments";
import { createInternshipReportComment } from "@/components/internship-map/extended-preview-report/service";
import { ResourceContext } from "@/contexts/resource-context";
import { useRouter } from "@bprogress/next";
import { ReportComplaint } from "@/components/doctor-training/report-complaint/report-complaint";
import { ReportManageActions } from "@/components/doctor-training/report-manage-actions/report-manage-actions";
import { createPath } from "@/service/routing/routing";
import { ReplyIcon, StarIcon, TriangleAlertIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export const ExtendedPreviewReport = ({
  report,
  facilitySlug,
}: {
  report: MedicalFacilityReport;
  facilitySlug: string;
}) => {
  const { details, anonTitle, rating, createdAt, comments } = report;
  const [showInput, setShowInput] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { logView } = useContext(ResourceContext);
  const router = useRouter();

  return (
    <Card className="active:98 @container transition-all hover:scale-102">
      <CardContent className={"@576:flex-row flex flex-col gap-12"}>
        <div>
          <div className={"mb-6"}>
            <div className={"mb-1 text-2xl font-bold text-zinc-950"}>
              {rating.toFixed(1)}
            </div>
            <div className="flex items-center justify-start gap-1">
              {Array.from({ length: rating }).map((_, i) => (
                <StarIcon
                  fill={"#FE9C08"}
                  stroke={"#FE9C08"}
                  size={"18px"}
                  key={i}
                />
              ))}
              {Array.from({ length: 5 - rating }).map((_, i) => (
                <StarIcon stroke={"#FE9C08"} size={"18px"} key={i} />
              ))}
            </div>
          </div>
          <Button variant={"outline"} onClick={() => setIsOpenModal(true)}>
            Więcej
          </Button>
        </div>
        <div>
          <div
            className={
              "mb-4 inline-flex items-center gap-2 text-sm text-zinc-500"
            }
          >
            <span className={"font-semibold"}>{anonTitle}</span>
            <span>·</span>
            <span>{createdAt}</span>
          </div>
          <div className={"flex w-full flex-col gap-4"}>
            {details.textAnswers.map(({ title, content }) => (
              <div key={title}>
                <div className={"text-sm font-semibold text-zinc-950"}>
                  {title}
                </div>
                <div className={"text-sm font-normal text-zinc-950"}>
                  {content}
                </div>
              </div>
            ))}
          </div>
          <div>
            <AnonComments
              comments={comments}
              onSend={() => {
                return createInternshipReportComment(
                  report.id,
                  logView,
                  router,
                );
              }}
              showInput={showInput}
            />
          </div>
          <div className={"mt-6 flex items-center gap-8 text-sm text-zinc-500"}>
            <div
              className={"flex items-center gap-2 hover:cursor-pointer"}
              onClick={() => {
                setShowInput(!showInput);
              }}
            >
              <ReplyIcon size="16px" />
              {showInput ? "Anuluj" : "Odpowiedz"}
            </div>

            {!report.canManage && (
              <ReportComplaint
                report={report}
                endpoint={"internship_report.complaint_add"}
              >
                <div className={"flex items-center gap-2 hover:cursor-pointer"}>
                  <TriangleAlertIcon size="16px" />
                  Zgłoś opinię
                </div>
              </ReportComplaint>
            )}

            {report.canManage && (
              <ReportManageActions
                editHref={createPath("internship_map.facility.report.edit", {
                  slug: facilitySlug,
                  reportId: report.id,
                })}
                deleteEndpoint={"internship_report.delete"}
                reportId={report.id}
              />
            )}
          </div>
        </div>

        <FullReport
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          report={report}
          facilitySlug={facilitySlug}
        />
      </CardContent>
    </Card>
  );
};
